import { useState} from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchMonthlyRevenueSingleUser, fetchMonthlyStatsAllUsers, fetchWeeklyRevenueAllUsers, exportToTelegram } from "../api/records";
import { GoPerson } from "react-icons/go";
import { useTranslation } from 'react-i18next';

import { useReportFilters } from "../hooks/useReportFilters";

import ReportListFilters from '../components/reports/ReportListFilters';
import WeeklyStats from "../components/reports/WeeklyStats";
import MonthlyStats from "../components/reports/MonthlyStats";
import MonthlyReportAll from "../components/reports/MonthlyReportAll";
import MonthlyReportSingle from "../components/reports/MonthlyReportSingle";
import DateRangeSheet from "../components/reports/DateRangeSheet";

const Reports = () => {
    const { t } = useTranslation();
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetMode, setSheetMode] = useState(null);
    
    const {
        filterAll, setFilterAll,
        filterSingle, setFilterSingle,
        filterAllDateFrom, setFilterAllDateFrom,
        filterAllDateTo, setFilterAllDateTo,
        filterSingleDateFrom, setFilterSingleDateFrom,
        filterSingleDateTo, setFilterSingleDateTo,
        currentUserId, setCurrentUserId,
        clearAllDates,
        clearSingleDates
     } = useReportFilters();
    
     // Queries
    const {data: monthly_stats_data_all = []} = useQuery({
            queryKey: ['monthly-revenue-all-users'],
            queryFn: fetchMonthlyStatsAllUsers
    });
    const {data: weekly_stats_data_all = []} = useQuery({
            queryKey: ['weekly-revenue-all-users'],
            queryFn: fetchWeeklyRevenueAllUsers
    });
    const {data: monthly_data_single = []} = useQuery({
            queryKey: ['monthly-revenue-single-user', { currentUserId, filterSingle, filterSingleDateFrom, filterSingleDateTo }],
            queryFn: () => fetchMonthlyRevenueSingleUser({ currentUserId, filterSingle, filterSingleDateFrom, filterSingleDateTo }),
            enabled: !!currentUserId && (filterSingle !== 'custom' || (!!filterSingleDateFrom && !!filterSingleDateTo))
    });

    const openSheet = (mode) => {
        setSheetMode(mode);
        setSheetOpen(true);
    }
    const handleExport = (params) => exportToTelegram(params);

    const sheetDateFrom = sheetMode === 'filterAll' ? filterAllDateFrom : filterSingleDateFrom;
    const sheetDateTo = sheetMode === 'filterAll' ? filterAllDateTo : filterSingleDateTo;
    const setSheetDateFrom = sheetMode === 'filterAll' ? setFilterAllDateFrom : setFilterSingleDateFrom;
    const setSheetDateTo = sheetMode === 'filterAll' ? setFilterAllDateTo : setFilterSingleDateTo;
    const clearSheetDates = sheetMode === 'filterAll' ? clearAllDates : clearSingleDates;

    return(
        <div className="flex flex-col w-full max-w-md mx-auto px-3 pt-4 pb-24">
            <h1 className="text-white text-3xl font-bold mb-4">{t('reports.reports')}</h1>
            {/* GENERAL STATS */}
            <p className="text-primary-10 text-xs font-mono mt-2 mb-3">{t('reports.general_report')}</p>
            <WeeklyStats data={weekly_stats_data_all} />
            <MonthlyStats data={monthly_stats_data_all} />

            {/* MONTHLY REPORT ALL */}
            <MonthlyReportAll 
                filter={filterAll}
                dateFrom={filterAllDateFrom}
                dateTo={filterAllDateTo}
                onFilterChange={setFilterAll}
                onCustomClick={() => { setFilterAll('custom'); openSheet('filterAll'); }}
                onClearDates={clearAllDates}
                onExport={() => handleExport({
                    reportType: 'monthly_report_all', 
                    filter: filterAll, 
                    dateFrom: filterAllDateFrom, 
                    dateTo: filterAllDateTo})} />
            {/* MONTHLY REPORT SINGLE */}
            <p className="text-primary-10 text-xs font-mono mt-2 mb-3">{t('reports.worker_report')}</p>
            <div className="flex gap-2 mb-1">
                <div className="rounded-xl p-2 text-2xl bg-success-50 text-success-800">
                    <GoPerson />
                </div>
                <ReportListFilters onChange={(filters) => 
                    setCurrentUserId(filters.worker)} />
            </div>
            <MonthlyReportSingle 
                currentUserId={currentUserId}
                filter={filterSingle}
                dateFrom={filterSingleDateFrom}
                dateTo={filterSingleDateTo}
                data={monthly_data_single}
                onFilterChange={setFilterSingle}
                onCustomClick={() => { setFilterSingle('custom'); openSheet('filterSingle'); }}
                onClearDates={clearSingleDates}
                onExport={() => handleExport({
                    reportType: 'monthly_report_single',
                    filter: filterSingle,
                    userId: currentUserId,
                    dateFrom: filterSingleDateFrom,
                    dateTo: filterSingleDateTo
                })}
            />
            <DateRangeSheet
                isOpen={sheetOpen}
                dateFrom={sheetDateFrom}
                dateTo={sheetDateTo}
                onDateFromChange={setSheetDateFrom}
                onDateToChange={setSheetDateTo}
                onClear={clearSheetDates}
                onApply={() => setSheetOpen(false)}
                />
        </div>
    );
};

export default Reports;